// Buyer Consulting Fee — the ONE place the two carve rates are turned into
// something a person reads. Both surfaces that render them import from here:
// the DEAL-side authorization card (the authoring surface at intake) and the
// LOAN-side terms card (read-only, once the deal has closed).
//
// 🔴 It is shared rather than duplicated because the two must never disagree.
// The rates are stored as FRACTIONS in a `decimal(9,6)` column (0.020000 = 2%),
// and this repo has NO shared percent formatter — `design-system/lib/format.ts`
// offers only `formatProgressPercent`, which CLAMPS to [0,1] and would silently
// render any rate above 100% as "100%", so it is not a rate renderer at all.
// What exists instead is a long tail of independently-rolled per-file helpers
// (the exact count depends on how you grep, which is the problem, not a detail
// — so this comment states none). Two of them are BOTH exported as `formatPct`
// with OPPOSITE unit contracts: `pages/capital-markets/shared/format.ts`
// multiplies by 100, `pages/ledgers/ledgerHelpers.ts` does not. Both are
// correct for their own callers, so it is a naming hazard rather than a live
// bug — but a one-argument call auto-completed to the wrong import is a 100x
// error with no type-system signal. One more ad-hoc copy on a money surface is
// not worth that risk.

import { formatRateFractionAsPercent } from "./rateFormat";

/**
 * Render a stored FRACTION (0.025) as a percent string ("2.5%"). Trims
 * trailing zeros, so 0.025 → "2.5%" and 0.05 → "5%". Four places of percent is
 * the same precision as the column's own six places of fraction.
 *
 * 0 renders as "0%", never blank and never an em-dash: the columns are NOT
 * NULL with a 0.00 off state, so zero is a real answer ("not charged"), not a
 * missing one.
 */
export function formatConsultingRatePercent(fraction: number): string {
  // Delegates to the shared primitive so a second fee's card cannot drift from
  // this one. Behaviour is byte-identical to the arithmetic that was here.
  return formatRateFractionAsPercent(fraction);
}

/**
 * PERCENT (2.5) → the FRACTION the API stores (0.025).
 *
 * The `toFixed(6)` is the column's own precision — `decimal(9,6)` — not a
 * tolerance: plain `x / 100` is exact for barely a quarter of the two-decimal
 * percentages in range (0.35 % divides to 0.0034999999999999996), and pinning
 * the result at six places normalises that IEEE-754 noise without ever
 * discarding a figure the column could hold.
 */
export function consultingRateToFraction(percent: number): number {
  return Number((percent / 100).toFixed(6));
}

/** FRACTION (0.025) → the percent string a form edits ("2.5"). Four places of
 *  percent is the same precision as six places of fraction. */
export function consultingRateToPercentInput(fraction: number): string {
  return String(Number((fraction * 100).toFixed(4)));
}
